import { useState } from "react";
import { FormData } from "./FormData.tsx";
import { SlSizeFullscreen } from "react-icons/sl";
import workspaceTypes from "../../../assets/WorkspaceTypes.tsx";
import DOMPurify from "dompurify";
import { FaStar } from "react-icons/fa";
import amenities from "../../../assets/Amenities.tsx";

interface WorkspaceSummaryProps {
  formData: FormData;
}

export default function WorkspaceSummary({ formData }: WorkspaceSummaryProps) {
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showAllAmenities, setShowAllAmenities] = useState(false);

  const getWorkspaceTypeName = (value: number) => {
    return (
      workspaceTypes.find((type) => type.value === value)?.title ||
      "Not specified"
    );
  };

  const getWorkspaceAmenities = () => {
    if (
      !formData.workspaceAmenities ||
      formData.workspaceAmenities.length === 0
    ) {
      return [];
    }
    return formData.workspaceAmenities
      .map((amenityId) => amenities.find((a) => a.value === amenityId))
      .filter((amenity): amenity is NonNullable<typeof amenity> => Boolean(amenity));
  };

  const IconComponent =
    workspaceTypes.find((type) => type.value === formData.workspaceTypeId)
      ?.image || null;
  const SafeHTML = ({ html }: { html: string }) => (
    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />
  );

  const workspaceAmenities = getWorkspaceAmenities();

  return (
    <div className="bg-gray-50 min-h-screen workspacePage-container mx-auto">
      <div className="lg:mx-30 xl:mx-40">
        <h2 className="text-3xl font-bold mb-4 pt-4">
          Review Your Workspace Listing
        </h2>
        <hr className="border-t border-gray-500 my-4 w-full shadow-gray-400" />

        {/* Image Carousel */}
        {formData.images && Array.isArray(formData.images) && formData.images.length > 0 && (
          <div className="flex flex-col mt-10 md:flex-row w-full h-auto md:h-[300px] lg:h-[500px]">
            {/* Main image - full width on mobile, 2/3 on desktop */}
            <div className="w-full md:w-1/2 h-64 md:h-full p-1">
              {formData.images[0]?.imageUrl && (
                <img
                  src={formData.images[0].imageUrl}
                  alt="Main workspace image"
                  className="w-full h-full object-cover rounded-t-4xl md:rounded-l-4xl md:rounded-tr-none"
                />
              )}
            </div>

            {/* Thumbnail grid - hidden on mobile, 1/3 on desktop */}
            <div className="hidden md:flex md:flex-col md:w-1/2 h-full p-1">
              {/* First row of thumbnails - with gap */}
              <div className="flex flex-row h-1/2 pb-1">
                {formData.images.slice(1, 3).map((image, index) => (
                  <div
                    key={index}
                    className="relative w-1/2 h-full"
                    style={{
                      paddingRight: index === 0 ? "0.25rem" : 0,
                      paddingLeft: index === 1 ? "0.25rem" : 0,
                    }}
                  >
                    {image?.imageUrl && (
                      <img
                        src={image.imageUrl}
                        alt={`Workspace thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                        style={{
                          borderTopRightRadius: index === 1 ? "2rem" : 0,
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* Second row of thumbnails - with gap */}
              <div className="flex flex-row h-1/2 pt-1">
                {formData.images.slice(3, 5).map((image, index) => (
                  <div
                    key={index + 2}
                    className="relative w-1/2 h-full"
                    style={{
                      paddingRight: index === 0 ? "0.25rem" : 0,
                      paddingLeft: index === 1 ? "0.25rem" : 0,
                    }}
                  >
                    {image?.imageUrl && (
                      <img
                        src={image.imageUrl}
                        alt={`Workspace thumbnail ${index + 3}`}
                        className="w-full h-full object-cover"
                        style={{
                          borderBottomRightRadius: index === 1 ? "2rem" : 0,
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Information */}
        <div className="mt-5 flex flex-row justify-between items-start">
          <div className="w-full">
            {/* Address/Ratings/Common Features */}
            <div className="flex flex-row w-full justify-between items-start">
              <div className="flex flex-row justify-between">
                <div className="mb-10">
                  {/* Title */}
                  <div className="mb-5">
                    <h6 className="text-4xl text-gray-700 mb-4">
                      {formData.title || "Workspace Title"}
                    </h6>
                  </div>
                  <div className="space-y-4">
                    {/* Row 1: Ratings, Reviews, Address */}
                    <div className="grid grid-cols-[1fr_1fr_3fr] gap-4 items-center">
                      {/* Column 1: Rating */}
                      <div className="flex items-center">
                        <FaStar className="mr-2" />
                        <p>New Listing</p>
                      </div>

                      {/* Column 2: Reviews */}
                      <div className="flex items-center">
                        <p>No Reviews</p>
                      </div>

                      {/* Column 3: Address */}
                      <div className="col-span-1 md:col-span-1">
                        <p className="truncate">
                          {formData.address || "Address"},{" "}
                          {formData.city || "City"}, {formData.state || "State"}{" "}
                          {formData.postalCode || "Postal Code"}
                        </p>
                      </div>
                    </div>

                    {/* Row 2: Features */}
                    <div className="grid grid-cols-[1fr_1fr_3fr] gap-4">
                      {/* Capacity */}
                      <div className="flex items-center justify-left py-2 rounded-md">
                        <svg
                          className="w-5 h-5 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                          />
                        </svg>
                        <span>{formData.capacity || "N/A"}</span>
                      </div>

                      {/* Size */}
                      <div className="flex items-center justify-left py-2 rounded-md">
                        <SlSizeFullscreen className="mr-2" />
                        <span>{formData.sizeSqm || "N/A"} m²</span>
                      </div>

                      {/* Workspace Type */}
                      <div className="flex items-center justify-left py-2 rounded-md">
                        {IconComponent && (
                          <IconComponent className="w-5 h-5 mr-2" />
                        )}
                        <span>
                          {formData.workspaceTypeId ? getWorkspaceTypeName(formData.workspaceTypeId) : "Not specified"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="mb-15">
              <h2 className="text-2xl mb-4">About This Space</h2>
              <div className="relative">
                <div
                  className={
                    showFullDescription ? "" : "overflow-hidden line-clamp-3"
                  }
                  style={
                    showFullDescription
                      ? {}
                      : {
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                        }
                  }
                >
                  <SafeHTML
                    html={formData.description || "No description provided"}
                  />
                </div>
                {!showFullDescription &&
                  (formData.description?.length || 0) > 50 && (
                    <div className="absolute bottom-0 left-0 w-full h-8 bg-gradient-to-t from-gray-50 to-transparent pointer-events-none"></div>
                  )}
              </div>
              {(formData.description?.length || 0) > 50 && (
                <button
                  className="mt-2 text-lg underline"
                  onClick={() => setShowFullDescription((prev) => !prev)}
                >
                  {showFullDescription ? "View Less" : "View More"}
                </button>
              )}
            </div>

            {/* Amenities */}
            <div className="container mt-4">
              <div className="row">
                <div className="col-md-8">
                  <div className="mb-10">
                    <h2 className="text-2xl mb-10">Amenities</h2>

                    {/* Display first 6 amenities (3 columns x 2 rows) */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-8 mb-4">
                      {workspaceAmenities.slice(0, 6).map((amenity) => {
                        if (!amenity) return null;
                        return (
                          <div
                            key={amenity.value}
                            className="flex items-center"
                          >
                            <span className="mr-2 flex-shrink-0">
                              {amenity.icon || <span className="w-6 h-6" />}
                            </span>
                            {amenity.title}
                          </div>
                        );
                      })}
                    </div>

                    {/* Show "View all amenities" link if there are more than 6 amenities */}
                    {workspaceAmenities.length > 6 && !showAllAmenities && (
                      <button
                        className="text-lg underline mt-5 mb-4 hover:text-blue-600"
                        style={{ cursor: "pointer" }}
                        onClick={() => setShowAllAmenities(true)}
                      >
                        View all amenities
                      </button>
                    )}

                    {/* Expanded section for remaining amenities */}
                    {showAllAmenities && workspaceAmenities.length > 6 && (
                      <div className="mt-8">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-3 mb-4">
                          {workspaceAmenities.slice(6).map((amenity) => {
                            if (!amenity) return null;
                            return (
                              <div
                                key={amenity.value}
                                className="flex items-center"
                              >
                                <span className="mr-2 flex-shrink-0">
                                  {amenity.icon || <span className="w-6 h-6" />}
                                </span>
                                {amenity.title}
                              </div>
                            );
                          })}
                        </div>
                        <button
                          className="text-lg underline hover:text-blue-600"
                          style={{ cursor: "pointer" }}
                          onClick={() => setShowAllAmenities(false)}
                        >
                          View less
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
